import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { TOnlineBankWithProvider } from './BankSelectionModal.types';
import { ReconsentConnection } from './reconsent/getReconsentConnections';

export type ModalStep =
    | 'choose'
    | 'connect'
    | 'shareableLink'
    | 'success'
    | 'reconfirmConsent'
    | 'reconfirm'
    | 'reconsentLoading'
    | 'reauth'
    | 'confirmRevoke'
    | 'extendSuccess'
    | 'revokeSuccess'
    | 'reconsentError';

// A renewal completed at the bank, carried back into a freshly mounted flow.
export type InitialRenewal = {
    connection: ReconsentConnection;
    hasMoreToRenew: boolean;
};

const getInitialStep = (
    initialStep: ModalStep,
    initialRenewal: InitialRenewal | null
): ModalStep => {
    if (!initialRenewal) return initialStep;

    return initialRenewal.hasMoreToRenew ? 'reconfirm' : 'extendSuccess';
};

export const useBankSelectionFlow = (
    initialStep: ModalStep = 'choose',
    initialRenewal: InitialRenewal | null = null
) => {
    const [step, setStep] = useState<ModalStep>(
        getInitialStep(initialStep, initialRenewal)
    );
    const [selectedBank, setSelectedBank] =
        useState<TOnlineBankWithProvider | null>(null);
    const [reauthLink, setReauthLink] = useState<string | null>(null);
    const [reauthConnection, setReauthConnection] =
        useState<ReconsentConnection | null>(null);
    const [revokeTarget, setRevokeTarget] =
        useState<ReconsentConnection | null>(null);
    const [extendedConnection, setExtendedConnection] =
        useState<ReconsentConnection | null>(
            initialRenewal && !initialRenewal.hasMoreToRenew
                ? initialRenewal.connection
                : null
        );
    const [extendedConnectionIds, setExtendedConnectionIds] = useState<
        string[]
    >(initialRenewal ? [initialRenewal.connection.onlineAccountId] : []);

    const selectBank = (bank: TOnlineBankWithProvider) => {
        tracking.ampli.trueLayerBankSelection({
            bankId: bank.bank_id,
            bankName: bank.bank_name
        });

        setSelectedBank(bank);
        setStep('connect');
    };

    const unselectBank = () => {
        setSelectedBank(null);
        setStep('choose');
    };

    const linkAnother = () => {
        setSelectedBank(null);
        setStep('choose');
    };

    const goToShareableLink = () => setStep('shareableLink');

    const goToReconfirm = () => {
        setRevokeTarget(null);
        setExtendedConnection(null);
        setStep('reconfirm');
    };
    const goToReconsentLoading = () => setStep('reconsentLoading');
    const goToError = () => setStep('reconsentError');

    const goToReauth = (link: string, connection: ReconsentConnection) => {
        setReauthLink(link);
        setReauthConnection(connection);
        setStep('reauth');
    };

    const goToConfirmRevoke = (connection: ReconsentConnection) => {
        setRevokeTarget(connection);
        setStep('confirmRevoke');
    };

    const goToExtendSuccess = (connection: ReconsentConnection) => {
        setExtendedConnection(connection);
        setStep('extendSuccess');
    };

    const goToRevokeSuccess = (connection: ReconsentConnection) => {
        setRevokeTarget(connection);
        setStep('revokeSuccess');
    };

    const cancelRevoke = () => {
        setRevokeTarget(null);
        setExtendedConnection(null);
        setStep('reconfirm');
    };

    const markExtended = (onlineAccountId: string) =>
        setExtendedConnectionIds((ids) =>
            ids.includes(onlineAccountId) ? ids : [...ids, onlineAccountId]
        );

    const goToChoose = () => {
        setRevokeTarget(null);
        setExtendedConnection(null);
        setSelectedBank(null);
        setExtendedConnectionIds([]);
        setStep('choose');
    };

    return {
        step,
        selectedBank,
        reauthLink,
        reauthConnection,
        revokeTarget,
        extendedConnection,
        isRevoking: revokeTarget !== null,
        extendedConnectionIds,
        selectBank,
        unselectBank,
        linkAnother,
        goToShareableLink,
        goToReconfirm,
        goToReconsentLoading,
        goToError,
        goToReauth,
        goToConfirmRevoke,
        goToExtendSuccess,
        goToRevokeSuccess,
        cancelRevoke,
        markExtended,
        goToChoose
    };
};
